import { OpenPanel, type TrackHandlerPayload, type TrackProperties } from '@openpanel/web';
import { getAbBucket } from './getAbBucket/getAbBucket';
import { getTrafficSegment } from './getTrafficSegment/getTrafficSegment';
import { isBotTraffic } from './isBotTraffic/isBotTraffic';
import { PUBLIC_APP_ENV } from '$app/env/public';

/**
 * Drops automated traffic before it reaches OpenPanel (HB-957).
 *
 * This lives on the SDK's `filter` option rather than in `track` below because
 * the noisiest events — the auto-tracked screen views that drive entry points,
 * path length and exit pages — never pass through our own wrapper. `filter`
 * is the one chokepoint every payload type crosses.
 *
 * Returning `true` keeps the payload.
 */
export const shouldTrackPayload = (payload: TrackHandlerPayload): boolean => {
	if (isBotTraffic()) {
		return false;
	}

	// Stamped onto the payload here, rather than via `setGlobalProperties` after
	// construction, because `trackScreenViews: true` makes the OpenPanel
	// constructor fire the session's first screen view synchronously — a global
	// set on the next line lands too late for that entry-point pageview, which is
	// the row entry-point analysis most depends on. `filter` runs inside `send()`
	// before the payload is serialised, and writing to the payload (rather than
	// calling back into `op`) keeps this free of the temporal-dead-zone error
	// that referencing `op` from its own constructor would raise (HB-957).
	if (payload.type === 'track') {
		const abBucket = getAbBucket();

		payload.payload.properties = {
			...(payload.payload.properties ?? {}),
			traffic_segment: getTrafficSegment(),
			// Lets any event, the entry-point screen view included, be split by A/B
			// arm: a test with n arms shows arm `ab_bucket % n`.
			...(abBucket !== undefined && { ab_bucket: abBucket })
		};
	}

	return true;
};

let op: OpenPanel | undefined;

/**
 * The OpenPanel client, or `undefined` until `initAnalytics` has run.
 *
 * Callers must tolerate `undefined`: every app that shares global-v5 imports
 * this module, but only the public websites opt into analytics.
 */
export const getAnalytics = () => op;

/**
 * Starts OpenPanel for the current app. Idempotent and browser-only.
 *
 * The client used to be constructed at module load, so any app importing a
 * shared component that tracks anything (the shared LoginPage, via form-utils)
 * silently started auto-tracking screen views into the public websites'
 * project, internal tools included. Opting in is now explicit: call this from
 * the `init` hook in the app's `hooks.client.ts`, which runs before hydration
 * so that components tracking on mount and the entry-point screen view are
 * not lost.
 */
export const initAnalytics = () => {
	if (op || typeof window === 'undefined') return op;

	op = new OpenPanel({
		apiUrl:
			PUBLIC_APP_ENV === 'production'
				? 'https://a.heni.com/api'
				: 'https://robust-event.no-zero.net/api',
		clientId:
			PUBLIC_APP_ENV === 'production'
				? '577dcfbe-9f98-4bb3-9a52-6f697c6b8dcf'
				: '5e8a998e-5d8a-4225-be86-e1257d495904',
		trackScreenViews: true,
		trackOutgoingLinks: true,
		trackAttributes: true,
		filter: shouldTrackPayload
	});

	dedupeHashOnlyScreenViews(op);

	return op;
};

/** Strips the `#fragment` so two urls differing only by anchor compare equal. */
const withoutHash = (url: string) => url.split('#')[0];

/**
 * De-dupes hash-only screen views (see HB-965).
 *
 * Anchor buttons such as BUY NOW append `#cards` to the url, which SvelteKit
 * routes through `pushState`. OpenPanel's own `screenView` de-dupe compares the
 * full `window.location.href`, so the anchor reads as a new page and fires a
 * second `screen_view` for a page the visitor never left — inflating unique
 * page views.
 *
 * This cannot be fixed downstream: bronze strips the fragment from the path, so
 * the duplicate lands as an identical-path repeat that is indistinguishable
 * from a genuine re-visit. Hence the client-side wrap here.
 *
 * We shadow the instance method rather than disabling `trackScreenViews`, so the
 * SDK keeps ownership of the history patching and listener wiring and we only
 * override the de-dupe key.
 *
 * The forwarded call always passes the hash-stripped url explicitly. That keeps
 * the SDK's own `lastPath` in lockstep with ours — if we let it fall back to
 * `window.location.href` it would still hold the pre-anchor url and would then
 * wrongly swallow a genuine later return to that same page.
 */
const dedupeHashOnlyScreenViews = (client: OpenPanel) => {
	const screenView = client.screenView.bind(client);
	// The constructor already tracked the entry page view before we could wrap.
	let lastTrackedUrl = withoutHash(window.location.href);

	client.screenView = ((...args: Parameters<typeof client.screenView>) => {
		const [first, second] = args;
		const isPathCall = typeof first === 'string';
		const url = withoutHash(isPathCall ? first : window.location.href);

		if (url === lastTrackedUrl) return;

		lastTrackedUrl = url;
		screenView(url, isPathCall ? second : first);
	}) as typeof client.screenView;
};

export const track = (event: string, props?: TrackProperties) => {
	op?.track(event as unknown as string, {
		env: !PUBLIC_APP_ENV || ['dev', 'local'].includes(PUBLIC_APP_ENV) ? 'dev' : PUBLIC_APP_ENV,
		// OpenPanel only attaches the page url to its own screen_view events,
		// so every custom event carries it explicitly.
		...(typeof window === 'undefined' ? {} : { url: window.location.href }),
		...(props || {})
	});
};

// Global properties (rather than per-event props) so they also ride on the
// auto-tracked screen views. Browser-only — call from the root layout's onMount.
export const setBreakpointGlobalProperties = () => {
	op?.setGlobalProperties({
		viewport_width: window.innerWidth,
		screen_w: window.screen.width,
		screen_h: window.screen.height
	});
};
